import { useLanguage } from "./i18n";
import { useState } from "react";
import { candidateName, type Unit } from "./rank_api";

const POSES = ["close", "open"] as const;
const VIEWS = ["front", "iso", "top"] as const;
type View = (typeof VIEWS)[number];
const POSE_LABEL: Record<(typeof POSES)[number], string> = { close: "关闭", open: "打开" };
const VIEW_LABEL: Record<View, string> = { front: "正面", iso: "斜视", top: "俯视" };

/** `src` is what the grid loads; `fullSrc` is fetched only when the participant zooms in. */
export function EvidenceImage({ src, fullSrc, label, onLoad }: { src: string; fullSrc?: string; label: string; onLoad?: () => void }) {
  const { t } = useLanguage();
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const url = attempt ? src + "?retry=" + attempt : src;
  const zoomUrl = fullSrc ?? src;
  return <>
    <button type="button" className="rs-image-button" aria-label={t("放大") + label} onClick={() => setExpanded(true)} disabled={error}>
      <img src={url} alt={label} onLoad={() => { setError(false); onLoad?.(); }} onError={() => setError(true)} />
      <span className="rs-image-zoom">↗</span>
    </button>
    {error && <button type="button" className="rs-image-retry" onClick={() => { setError(false); setAttempt((n) => n + 1); }}>{t("图片加载失败 · 重试")}</button>}
    {expanded && <div className="rs-lightbox" role="dialog" aria-modal="true" aria-label={label} onClick={() => setExpanded(false)}>
      <button type="button" onClick={() => setExpanded(false)} autoFocus>{t("关闭 ×")}</button><img src={zoomUrl} alt={label} />
    </div>}
  </>;
}

/**
 * Reference block: the original prompt plus the generation input images. Schema 3 published
 * only the open state; a study built after that carries the closed one too, from the same
 * source render, so the pair is laid out like a candidate card's closed/open pair.
 */
export function ReferencePanel({ unit }: { unit: Unit }) {
  const { t } = useLanguage();
  const closed = unit.references.input_close;
  const opened = unit.references.input;
  const states: { pose: (typeof POSES)[number]; src: string; full: string }[] = [];
  if (closed) states.push({ pose: "close", src: unit.references.input_close_thumb ?? closed, full: closed });
  if (opened) states.push({ pose: "open", src: unit.references.input_thumb ?? opened, full: opened });
  return <section className="rs-reference">
    <div className={"rs-gt" + (states.length > 1 ? " rs-gt-pair" : "")}><span className="rs-eyebrow">{t("参考图像")}</span>
      <div className="rs-gt-states">{states.map(({ pose, src, full }) =>
        <figure key={pose} className="rs-candidate-pose">
          <EvidenceImage src={src} fullSrc={full} label={t("参考图像（生成输入）") + " · " + t(POSE_LABEL[pose])} />
          {states.length > 1 && <figcaption>{t(pose === "open" ? "打开 · 输入" : POSE_LABEL[pose])}</figcaption>}
        </figure>)}</div></div>
    <div className="rs-brief"><span className="rs-eyebrow">{t("目标描述")}</span>
      {unit.prompt.trim()
        ? <details open><summary>{t("查看描述")}</summary><p>{unit.prompt}</p></details>
        : <p className="rs-brief-empty">{t("这个物体没有文字描述，请以参考图像为准。")}</p>}
    </div>
  </section>;
}

/** One card per anonymous candidate, each showing its closed and open render for the selected view. */
export default function EvidencePanel({ unit, onViewed, admin = false }: { unit: Unit; onViewed: (token: string, view: string) => void; admin?: boolean }) {
  const { t } = useLanguage();
  const [view, setView] = useState<View>("iso");
  const [largeImages, setLargeImages] = useState(false);
  return <section className={"rs-evidence" + (largeImages ? " rs-large-images" : "")}>
    <div className="rs-section-heading"><div><span className="rs-step">01</span><h2>{t("对比渲染图")}</h2><p>{t("每个产物一张关闭、一张打开；同视角横向比较，点击放大。")}</p></div>
      <div className="rs-view-controls">
        <button type="button" className="rs-gallery-size rs-pill-toggle" aria-pressed={largeImages} onClick={() => setLargeImages((value) => !value)}>{t(largeImages ? "紧凑视图" : "大图查看")}</button>
        <div className="rs-segment" role="group" aria-label={t("视角")}>{VIEWS.map((value) =>
          <button key={value} type="button" data-view={value} className={view === value ? "selected" : ""} aria-pressed={view === value}
            onClick={() => setView(value)}>{t(VIEW_LABEL[value])}</button>)}</div>
      </div>
    </div>
    <div className="rs-candidate-grid" data-view={view}>{unit.candidates.map((item) => <article className="rs-candidate" key={item.token} data-candidate-card={item.label}>
      <header><span className={"rs-badge badge-" + item.label}>{item.label}</span><strong>{t("产物")} {candidateName(item, admin)}</strong><small>{t(VIEW_LABEL[view])}</small></header>
      <div className="rs-candidate-pair">{POSES.map((pose) => {
        const key = pose + "_" + view;
        return <figure key={key} className="rs-candidate-pose">
          <EvidenceImage src={item.thumbnails?.[key] ?? item.images[key]!} fullSrc={item.images[key]!} label={t("产物 ") + item.label + " · " + t(POSE_LABEL[pose]) + " · " + t(VIEW_LABEL[view])} onLoad={() => onViewed(item.token, key)} />
          <figcaption>{t(POSE_LABEL[pose])}</figcaption>
        </figure>;
      })}</div>
    </article>)}</div>
  </section>;
}
